/**
 * Admin → Sites tab.
 *
 * Ported from the legacy `routes/admin.ts` Sites section. Lists every site
 * with status/search filters; an inline expanding "Edit Site" panel covers
 * settings, ownership transfer, commission overrides, and cloning. The
 * "payment link" button hands off to the per-site billing page.
 */
import { useEffect, useMemo, useRef, useState } from 'hono/jsx';
import { AdminShell } from '../../components/AdminShell';
import { api, escapeHtml } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { navigate } from '../../lib/router';
import { Spinner } from '../../components/Spinner';

interface AdminSite {
  id: string;
  name: string;
  slug: string;
  siteStatus: string;
  isFree?: boolean;
  buildSkill?: string | null;
  pendingOwnerEmail?: string | null;
  user: { id: string; name?: string | null; email: string };
  plan?: { id: string; name: string; setupFee: number; subscription: number; interval: string; setupPct: number; recurringPct: number } | null;
  subscription?: { id: string; status: string; cancelAtPeriodEnd?: boolean; currentPeriodEnd?: string | null; commissionOverrides?: { setupPct: number | null; recurringPct: number | null } } | null;
  affiliate?: { id: string; name: string; code: string; setupPct: number | null; recurringPct: number | null } | null;
  effectiveCommission?: { setupPct: number | null; recurringPct: number | null } | null;
}

// Legacy sites.build_skill values -> engine-pair profile ids (mirrors the
// API's build-profiles.yml legacy_aliases; the API also normalizes on save).
const BUILD_SKILL_LEGACY: Record<string, string> = {
  'build-template-lite': 'sonnet-5+pi-kimi',
  'lite': 'sonnet-5+pi-kimi',
  'lite-opus-46': 'sonnet-5+pi-kimi',
  'lite-opus-47': 'sonnet-5+pi-kimi',
  'standard': 'sonnet-5+pi-kimi',
  'build-template': 'opus-5+sonnet-5',
  'full': 'opus-5+sonnet-5',
  'full-opus-46': 'opus-5+sonnet-5',
  'full-opus-47': 'opus-5+sonnet-5',
  'full-opus-47-high': 'opus-5+sonnet-5',
  'power': 'opus-5+sonnet-5',
  'pi-lab': 'pi-kimi+pi-kimi',
  'free': 'pi-kimi+pi-kimi',
};

// Build engines (mirror of the API's build-profiles.yml engines map).
export const BUILD_ENGINES: { id: string; name: string }[] = [
  { id: 'pi-kimi', name: 'pi (Kimi K2.7)' },
  { id: 'pi-qwen', name: 'pi (Qwen 3.8 27B)' },
  { id: 'sonnet-5', name: 'Claude Sonnet 5' },
  { id: 'opus-5', name: 'Claude Opus 5 (high quality)' },
];

function mainDomain(): string {
  if (typeof window === 'undefined') return 'webforcheap.com';
  const host = window.location.hostname;
  if (host.endsWith('appsforcheap.com')) return 'appsforcheap.com';
  return 'webforcheap.com';
}

export function AdminSites() {
  const [sites, setSites] = useState<AdminSite[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [editing, setEditing] = useState<AdminSite | null>(null);
  const searchTimer = useRef<number | null>(null);

  async function load(s = status, q = search) {
    setLoading(true);
    setLoadError(null);
    let url = '/admin/sites?limit=50';
    if (s) url += '&status=' + encodeURIComponent(s);
    if (q) url += '&search=' + encodeURIComponent(q);
    try {
      const r = await api(url);
      if (!r.ok) throw new Error('Failed to load sites');
      const data = await r.json() as { sites?: AdminSite[] };
      setSites(data.sites || []);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load sites');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load('', ''); }, []);

  function onStatusChange(e: Event) {
    const v = (e.target as HTMLSelectElement).value;
    setStatus(v);
    load(v, search);
  }

  function onSearchInput(e: Event) {
    const v = (e.target as HTMLInputElement).value;
    setSearch(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(status, v), 300) as unknown as number;
  }

  return (
    <AdminShell active="sites">
      <div class="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-slate-100">All Sites</h2>
        <div class="flex gap-2">
          <select
            value={status}
            onChange={onStatusChange}
            class="px-3 py-1.5 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          >
            <option value="">All Status</option>
            <option value="free">Free Sites</option>
            <option value="active">Active Subscription</option>
            <option value="past_due">Past Due</option>
            <option value="canceled">Canceled</option>
            <option value="unpaid">No Subscription</option>
          </select>
          <input
            type="text"
            value={search}
            onInput={onSearchInput}
            placeholder="Search sites..."
            class="px-3 py-1.5 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          />
        </div>
      </div>

      {editing && (
        <EditSitePanel
          site={editing}
          onClose={() => setEditing(null)}
          onReload={() => load(status, search)}
        />
      )}

      <div class="bg-dark-800 rounded-xl border border-slate-700/50 overflow-x-auto">
        {loading ? (
          <div class="p-8 text-center"><Spinner className="mx-auto" /><p class="text-slate-400 mt-4 text-sm">Loading sites...</p></div>
        ) : loadError ? (
          <div class="p-8 text-center text-red-400">{loadError}</div>
        ) : !sites.length ? (
          <div class="p-8 text-center text-slate-400">No sites found</div>
        ) : (
          <SitesTable sites={sites} onEdit={(s) => setEditing(s)} />
        )}
      </div>
    </AdminShell>
  );
}

function SitesTable({ sites, onEdit }: { sites: AdminSite[]; onEdit: (s: AdminSite) => void }) {
  const statusColors: Record<string, string> = {
    active: 'bg-emerald-500/20 text-emerald-400',
    past_due: 'bg-amber-500/20 text-amber-400',
    canceled: 'bg-red-500/20 text-red-400',
    trialing: 'bg-purple-500/20 text-purple-400',
  };

  return (
    <table class="w-full">
      <thead class="bg-dark-700">
        <tr>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Site</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Owner</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Plan</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Subscription</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Affiliate</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Commission</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-slate-700/50">
        {sites.map((s) => {
          const statusColor = s.subscription
            ? (statusColors[s.subscription.status] || 'bg-slate-500/20 text-slate-400')
            : 'bg-slate-500/20 text-slate-400';
          const overrides = s.subscription?.commissionOverrides || { setupPct: null, recurringPct: null };
          const hasOverride = overrides.setupPct !== null || overrides.recurringPct !== null;
          return (
            <tr class="hover:bg-dark-700/50">
              <td class="px-4 py-3">
                <div class="text-sm font-medium text-slate-200">{s.name}</div>
                <div class="text-xs text-slate-500">{s.slug}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-slate-200">{s.user.name || 'Unknown'}</div>
                <div class="text-xs text-slate-500">{s.user.email}</div>
              </td>
              <td class="px-4 py-3">
                {s.isFree && (
                  <span class="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-400">Free</span>
                )}
                {s.plan ? (
                  <>
                    <div class="text-sm text-cyan-400">{s.plan.name}</div>
                    <div class="text-xs text-slate-500">${s.plan.setupFee} setup / ${s.plan.subscription}/{s.plan.interval}</div>
                  </>
                ) : <span class="text-xs text-slate-500">No plan</span>}
              </td>
              <td class="px-4 py-3">
                {s.subscription ? (
                  <>
                    <span class={`text-xs px-2 py-1 rounded ${statusColor}`}>
                      {s.subscription.status}{s.subscription.cancelAtPeriodEnd ? ' (canceling)' : ''}
                    </span>
                    {s.subscription.currentPeriodEnd && (
                      <div class="text-xs text-slate-500 mt-1">
                        Renews: {new Date(s.subscription.currentPeriodEnd).toLocaleDateString()}
                      </div>
                    )}
                  </>
                ) : <span class="text-xs text-slate-500">None</span>}
              </td>
              <td class="px-4 py-3">
                {s.affiliate ? (
                  <>
                    <div class="text-sm text-slate-200">{s.affiliate.name}</div>
                    <code class="text-xs text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">{s.affiliate.code}</code>
                  </>
                ) : <span class="text-xs text-slate-500">Direct</span>}
              </td>
              <td class="px-4 py-3">
                {s.affiliate && s.effectiveCommission ? (
                  <>
                    <div class={`text-sm ${hasOverride ? 'text-amber-400' : 'text-slate-300'}`}>
                      {s.effectiveCommission.setupPct ?? 0}% / {s.effectiveCommission.recurringPct ?? 0}%
                    </div>
                    <div class="text-xs text-slate-500">{hasOverride ? 'Override' : 'Default'}</div>
                  </>
                ) : <span class="text-xs text-slate-500">N/A</span>}
              </td>
              <td class="px-4 py-3">
                <button
                  type="button"
                  onClick={() => onEdit(s)}
                  class="text-cyan-400 hover:text-cyan-300 text-sm"
                >Edit</button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function EditSitePanel({ site, onClose, onReload }: { site: AdminSite; onClose: () => void; onReload: () => void }) {
  // Build Profile = "<create-engine>+<edit-engine>". Split into the two
  // dropdowns; recomposed on save.
  const [initialCreate, initialEdit] = useMemo(() => {
    const raw = site.buildSkill || 'sonnet-5+pi-kimi';
    const pair = BUILD_SKILL_LEGACY[raw] || raw;
    const parts = pair.split('+');
    return parts.length === 2 && BUILD_ENGINES.some((e) => e.id === parts[0]) && BUILD_ENGINES.some((e) => e.id === parts[1])
      ? [parts[0], parts[1]]
      : ['sonnet-5', 'pi-kimi'];
  }, [site]);

  const [createEngine, setCreateEngine] = useState<string>(initialCreate);
  const [editEngine, setEditEngine] = useState<string>(initialEdit);
  const buildSkill = createEngine + '+' + editEngine;
  const [isFree, setIsFree] = useState<boolean>(!!site.isFree);
  const [savingSettings, setSavingSettings] = useState<boolean>(false);

  // hono/jsx/dom sets <select value=...> as a property before the <option>
  // children are appended, so the browser ignores the value and falls back
  // to the first option. Re-apply the values after mount and on change.
  const createSelectRef = useRef<HTMLSelectElement>(null);
  const editSelectRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (createSelectRef.current && createSelectRef.current.value !== createEngine) {
      createSelectRef.current.value = createEngine;
    }
    if (editSelectRef.current && editSelectRef.current.value !== editEngine) {
      editSelectRef.current.value = editEngine;
    }
  }, [createEngine, editEngine]);

  const [newOwnerEmail, setNewOwnerEmail] = useState<string>('');
  const [transferBusy, setTransferBusy] = useState<boolean>(false);
  const [pendingOwner, setPendingOwner] = useState<string | null>(site.pendingOwnerEmail || null);

  const [cloneName, setCloneName] = useState<string>(site.name + ' copy');
  const [cloneSubdomain, setCloneSubdomain] = useState<string>((site.slug + '-copy').slice(0, 50));
  const [cloneBusy, setCloneBusy] = useState<boolean>(false);
  const [cloneResult, setCloneResult] = useState<{ ok: boolean; message: string; warnings: string[] } | null>(null);

  const overrides = site.subscription?.commissionOverrides || { setupPct: null, recurringPct: null };
  const [setupPct, setSetupPct] = useState<string>(overrides.setupPct == null ? '' : String(overrides.setupPct));
  const [recurringPct, setRecurringPct] = useState<string>(overrides.recurringPct == null ? '' : String(overrides.recurringPct));

  const planRates = site.plan ? `${site.plan.setupPct}% / ${site.plan.recurringPct}%` : 'N/A';
  const affRates = site.affiliate && (site.affiliate.setupPct !== null || site.affiliate.recurringPct !== null)
    ? `${site.affiliate.setupPct ?? 'default'}% / ${site.affiliate.recurringPct ?? 'default'}%`
    : 'Using plan defaults';
  const effectiveRates = site.effectiveCommission
    ? `${site.effectiveCommission.setupPct ?? 0}% setup / ${site.effectiveCommission.recurringPct ?? 0}% recurring`
    : 'N/A';

  async function saveSettings() {
    setSavingSettings(true);
    const errors: string[] = [];
    const successes: string[] = [];
    try {
      const r = await api('/admin/sites/' + site.id + '/build-skill', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buildSkill }),
      });
      if (!r.ok) {
        const result = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(result.error || 'Failed to update build skill');
      }
      successes.push('build skill');
    } catch (e: any) {
      errors.push('Build skill: ' + e.message);
    }
    try {
      const r = await api('/admin/sites/' + site.id + '/free', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isFree }),
      });
      if (!r.ok) {
        const result = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(result.error || 'Failed to update free status');
      }
      successes.push('billing status');
    } catch (e: any) {
      errors.push('Billing: ' + e.message);
    }
    setSavingSettings(false);

    if (!errors.length) {
      showToast('success', 'Settings Saved', 'Site settings updated successfully');
      onReload();
      onClose();
    } else if (successes.length) {
      showToast('warning', 'Partial Update', 'Updated ' + successes.join(', ') + '. Errors: ' + errors.join('; '));
      onReload();
    } else {
      showToast('error', 'Save Failed', errors.join('; '));
    }
  }

  async function transferOwnership() {
    const email = newOwnerEmail.trim();
    if (!email) { showToast('error', 'Error', 'Please enter an email address'); return; }
    if (!confirm('Transfer ownership of this site to ' + email + '?')) return;
    setTransferBusy(true);
    try {
      const r = await api('/admin/sites/' + site.id + '/owner', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const result = await r.json() as { error?: string; transferred?: boolean; pending?: boolean };
      if (!r.ok) throw new Error(result.error || 'Failed to transfer ownership');

      if (result.transferred) {
        showToast('success', 'Ownership Transferred', 'Site transferred to ' + email);
      } else if (result.pending) {
        showToast('success', 'Transfer Pending', 'Site will be transferred when ' + email + ' signs up');
        setPendingOwner(email);
      }
      setNewOwnerEmail('');
      onReload();
      if (result.transferred) onClose();
    } catch (e: any) {
      showToast('error', 'Transfer Failed', e.message);
    } finally {
      setTransferBusy(false);
    }
  }

  async function cancelTransfer() {
    if (!confirm('Cancel pending ownership transfer?')) return;
    try {
      const r = await api('/admin/sites/' + site.id + '/owner', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: null }),
      });
      if (!r.ok) {
        const result = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(result.error || 'Failed to cancel transfer');
      }
      showToast('success', 'Transfer Cancelled', 'Pending ownership transfer has been cancelled');
      setPendingOwner(null);
      onReload();
    } catch (e: any) {
      showToast('error', 'Error', e.message);
    }
  }

  async function cloneSite() {
    const subdomain = cloneSubdomain.trim().toLowerCase();
    if (!subdomain) { showToast('error', 'Error', 'Enter a subdomain for the clone'); return; }
    if (!confirm(`Clone "${site.name}" into a new site at ${subdomain}? It will be owned by you.`)) return;
    setCloneBusy(true);
    setCloneResult(null);
    try {
      const r = await api('/admin/sites/' + site.id + '/clone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: cloneName.trim(), subdomain }),
      });
      const result = await r.json() as { error?: string; site?: any; warnings?: string[]; filesCopied?: number; repoFilesCopied?: number };
      if (!r.ok) throw new Error(result.error || 'Failed to clone site');

      const warnings = result.warnings || [];
      showToast(
        'success',
        'Site Cloned',
        `${result.site.slug} created (${result.filesCopied} files, ${result.repoFilesCopied} repo files)`,
      );
      const link = `Cloned to ${result.site.slug}` + (result.site.url ? '' : '');
      setCloneResult({ ok: true, message: link, warnings });
      onReload();
    } catch (e: any) {
      showToast('error', 'Clone Failed', e.message);
    } finally {
      setCloneBusy(false);
    }
  }

  async function clearCommissionOverrides() {
    if (!site.subscription) { showToast('error', 'Error', 'No subscription to update'); return; }
    try {
      const r = await api('/admin/subscriptions/' + site.subscription.id + '/commission', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setupPct: null, recurringPct: null }),
      });
      if (!r.ok) {
        const result = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(result.error || 'Failed to clear overrides');
      }
      onClose();
      showToast('success', 'Overrides Cleared', 'Site will use affiliate/plan defaults');
      onReload();
    } catch (e: any) {
      showToast('error', 'Error', e.message);
    }
  }

  async function saveCommission(e: Event) {
    e.preventDefault();
    if (!site.subscription) { showToast('error', 'Error', 'No subscription to update'); return; }
    const data: Record<string, number> = {};
    if (setupPct !== '') data.setupPct = parseInt(setupPct, 10);
    if (recurringPct !== '') data.recurringPct = parseInt(recurringPct, 10);
    if (!Object.keys(data).length) { showToast('error', 'Error', 'Please enter at least one rate'); return; }
    try {
      const r = await api('/admin/subscriptions/' + site.subscription.id + '/commission', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!r.ok) {
        const result = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(result.error || 'Failed to update commission');
      }
      onClose();
      showToast('success', 'Commission Updated', 'Site commission rates saved');
      onReload();
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    }
  }

  return (
    <div class="mb-6 bg-dark-800 rounded-xl border border-cyan-500/30 shadow-xl overflow-hidden">
      <div class="px-6 py-4 border-b border-slate-700/50 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-100">Edit Site</h3>
        <button type="button" onClick={onClose} class="text-slate-400 hover:text-slate-200" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="p-6 space-y-6">
        <div class="grid md:grid-cols-2 gap-6">
          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Site Information</h4>
            <div class="bg-dark-700 rounded-lg p-4 space-y-2">
              <div class="flex justify-between"><span class="text-xs text-slate-500">Name</span><span class="text-sm text-slate-200">{site.name}</span></div>
              <div class="flex justify-between"><span class="text-xs text-slate-500">Slug</span><span class="text-sm text-cyan-400">{site.slug}</span></div>
              <div class="flex justify-between"><span class="text-xs text-slate-500">Status</span><span class={'text-sm ' + (site.siteStatus === 'live' ? 'text-emerald-400' : 'text-amber-400')}>{site.siteStatus}</span></div>
              <div class="flex justify-between"><span class="text-xs text-slate-500">Owner</span><span class="text-sm text-slate-200">{(site.user.name || 'Unknown')} ({site.user.email})</span></div>
            </div>
          </div>

          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Transfer Ownership</h4>
            <div class="bg-dark-700 rounded-lg p-4">
              <label class="text-xs text-slate-400 block mb-2">New Owner Email</label>
              <div class="flex gap-2">
                <input
                  type="email"
                  value={newOwnerEmail}
                  onInput={(e) => setNewOwnerEmail((e.target as HTMLInputElement).value)}
                  placeholder="newowner@example.com"
                  class="flex-1 px-3 py-2 bg-slate-900 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
                <button
                  type="button"
                  onClick={transferOwnership}
                  disabled={transferBusy}
                  class="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-dark-900 rounded-lg font-medium text-sm transition-colors whitespace-nowrap disabled:opacity-60"
                >{transferBusy ? 'Transferring...' : 'Transfer'}</button>
              </div>
              <p class="text-xs text-slate-500 mt-2">If user exists, transfers immediately. Otherwise, transfers when they sign up.</p>
              {pendingOwner && (
                <div class="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
                  Pending transfer to: {pendingOwner}
                  <button type="button" onClick={cancelTransfer} class="ml-2 underline hover:no-underline">Cancel</button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div class="grid md:grid-cols-2 gap-6">
          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Build Profile</h4>
            <div class="bg-dark-700 rounded-lg p-4 space-y-3">
              <div>
                <label class="text-xs text-slate-400 block mb-2">Initial build &amp; rebuilds</label>
                <select
                  ref={createSelectRef}
                  value={createEngine}
                  onChange={(e) => setCreateEngine((e.target as HTMLSelectElement).value)}
                  class="w-full px-3 py-2.5 bg-slate-900 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 cursor-pointer"
                >
                  {BUILD_ENGINES.map((e) => <option value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <label class="text-xs text-slate-400 block mb-2">Updates &amp; edits</label>
                <select
                  ref={editSelectRef}
                  value={editEngine}
                  onChange={(e) => setEditEngine((e.target as HTMLSelectElement).value)}
                  class="w-full px-3 py-2.5 bg-slate-900 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 cursor-pointer"
                >
                  {BUILD_ENGINES.map((e) => <option value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <p class="text-xs text-slate-500">Profile: <code class="text-slate-400">{buildSkill}</code> — e.g. build with Opus, manage with Sonnet. Trivial text swaps always use the instant edge lane.</p>
            </div>
          </div>

          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Billing Override</h4>
            <div class="bg-dark-700 rounded-lg p-4">
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isFree}
                  onChange={(e) => setIsFree((e.target as HTMLInputElement).checked)}
                  class="w-5 h-5 rounded border-2 border-slate-500 bg-slate-900 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
                />
                <div>
                  <span class="text-sm text-slate-200">Free Site</span>
                  <p class="text-xs text-slate-500">Bypass payment requirements and unlock publish options</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div class="pt-2">
          <button
            type="button"
            onClick={saveSettings}
            disabled={savingSettings}
            class="w-full bg-cyan-500 hover:bg-cyan-400 text-dark-900 py-2.5 rounded-lg font-semibold transition-colors disabled:opacity-60"
          >{savingSettings ? 'Saving...' : 'Save Site Settings'}</button>
        </div>

        <div>
          <h4 class="text-sm font-medium text-slate-300 mb-3">Payment Link</h4>
          <div class="bg-dark-700 rounded-lg p-4">
            <p class="text-xs text-slate-400 mb-3">Generate a payment link to send to the customer. Works before or after the site is built.</p>
            <button
              type="button"
              onClick={() => navigate('/sites/' + site.id + '/billing')}
              class="w-full flex items-center justify-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-500/30 transition-colors border border-emerald-500/30"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>
              Open Billing Page
            </button>
          </div>
        </div>

        {site.subscription && (
          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Subscription</h4>
            <div class="bg-dark-700 rounded-lg p-4 space-y-2">
              <div class="flex justify-between"><span class="text-xs text-slate-500">Plan</span><span class="text-sm text-slate-200">{site.plan ? `${site.plan.name} ($${site.plan.setupFee} / $${site.plan.subscription}/${site.plan.interval})` : 'Unknown'}</span></div>
              <div class="flex justify-between"><span class="text-xs text-slate-500">Status</span><span class={'text-sm ' + (site.subscription.status === 'active' ? 'text-emerald-400' : 'text-amber-400')}>{site.subscription.status}{site.subscription.cancelAtPeriodEnd ? ' (canceling)' : ''}</span></div>
              {site.subscription.currentPeriodEnd && (
                <div class="flex justify-between"><span class="text-xs text-slate-500">Renews</span><span class="text-sm text-slate-200">{new Date(site.subscription.currentPeriodEnd).toLocaleDateString()}</span></div>
              )}
            </div>
          </div>
        )}

        {site.affiliate && (
          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Affiliate</h4>
            <div class="bg-dark-700 rounded-lg p-4 space-y-2">
              <div class="flex justify-between"><span class="text-xs text-slate-500">Name</span><span class="text-sm text-slate-200">{site.affiliate.name}</span></div>
              <div class="flex justify-between"><span class="text-xs text-slate-500">Code</span><code class="text-xs text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">{site.affiliate.code}</code></div>
            </div>
          </div>
        )}

        {site.affiliate ? (
          <div>
            <h4 class="text-sm font-medium text-slate-300 mb-3">Commission Rates</h4>
            <div class="bg-dark-700/50 rounded-lg p-3 text-xs text-slate-400 space-y-1 mb-3">
              <div>Plan default: <span class="text-slate-300">{planRates}</span></div>
              <div>Affiliate rate: <span class="text-slate-300">{affRates}</span></div>
              <div>Effective: <span class="text-emerald-400 font-medium">{effectiveRates}</span></div>
            </div>
            <p class="text-xs text-slate-500 mb-3">Override commission rates for this site only. Leave blank to use defaults.</p>
            <form onSubmit={saveCommission} class="space-y-3">
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-1">Setup Fee %</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={setupPct}
                    onInput={(e) => setSetupPct((e.target as HTMLInputElement).value)}
                    placeholder="Default"
                    class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-1">Recurring %</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={recurringPct}
                    onInput={(e) => setRecurringPct((e.target as HTMLInputElement).value)}
                    placeholder="Default"
                    class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                  />
                </div>
              </div>
              <div class="flex gap-2">
                <button type="button" onClick={clearCommissionOverrides} class="flex-1 bg-slate-600 text-slate-200 py-2 rounded-lg text-sm font-medium hover:bg-slate-500 transition-colors">Clear Overrides</button>
                <button type="submit" class="flex-1 bg-cyan-500 text-dark-900 py-2 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors">Save Commission</button>
              </div>
            </form>
          </div>
        ) : (
          <div class="bg-dark-700/50 rounded-lg p-4 text-center">
            <p class="text-sm text-slate-400">This site has no affiliate - commission settings not applicable.</p>
          </div>
        )}

        <div class="pt-4 border-t border-slate-700/50">
          <h4 class="text-sm font-medium text-slate-300 mb-3">Clone Site</h4>
          <div class="bg-dark-700 rounded-lg p-4 space-y-3">
            <p class="text-xs text-slate-500">Creates an independent copy (files + repo) owned by you, on a new subdomain. The custom domain is not copied, and no rebuild is triggered. Use this to turn a built site into a reusable template.</p>
            <div>
              <label class="text-xs text-slate-400 block mb-1">New Name</label>
              <input
                type="text"
                value={cloneName}
                onInput={(e) => setCloneName((e.target as HTMLInputElement).value)}
                placeholder="My Template Copy"
                class="w-full px-3 py-2 bg-slate-900 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <div>
              <label class="text-xs text-slate-400 block mb-1">New Subdomain</label>
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  value={cloneSubdomain}
                  onInput={(e) => setCloneSubdomain((e.target as HTMLInputElement).value)}
                  placeholder="my-template-copy"
                  class="flex-1 px-3 py-2 bg-slate-900 border border-slate-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
                <span class="text-xs text-slate-500 whitespace-nowrap">.{mainDomain()}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={cloneSite}
              disabled={cloneBusy}
              class="w-full px-4 py-2 bg-violet-500 hover:bg-violet-400 text-dark-900 rounded-lg font-medium text-sm transition-colors disabled:opacity-60"
            >{cloneBusy ? 'Cloning…' : 'Clone Site'}</button>
            {cloneResult && (
              <div class="text-xs">
                <div class="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded text-emerald-400">{cloneResult.message}</div>
                {cloneResult.warnings.length > 0 && (
                  <div class="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-amber-400">
                    {cloneResult.warnings.map((w) => <div>{w}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// keep escapeHtml import quiet for tree-shake; it's available if needed
void escapeHtml;

export default AdminSites;
